// src/pages/Account.tsx
// Page MixMind : espace personnel pour afficher le profil courant, réglages globaux et accès à l’Espace famille.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ProfileForm, { ProfileFormValues } from "../components/account/ProfileForm";
import type { IndividualProfile } from "../data/fakeUsers";

export default function Account() {
  const {
    account,
    activeProfile,
    darkMode,
    setDarkMode,
    isIndividual,
    updateIndividualProfile,
  } = useAuth();

  const [editOpen, setEditOpen] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);

  const individualProfile = useMemo(
    () => (account?.type === "individual" ? account.profile : null),
    [account]
  );

  const [profileInitial, setProfileInitial] = useState<ProfileFormValues>({
    displayName: individualProfile?.firstName || "",
    role: "parent",
    age:
      individualProfile?.birthYear != null
        ? new Date().getFullYear() - individualProfile.birthYear
        : undefined,
    avatar: individualProfile?.avatar || "initial",
  });

  useEffect(() => {
    setProfileInitial({
      displayName: individualProfile?.firstName || "",
      role: "parent",
      age:
        individualProfile?.birthYear != null
          ? new Date().getFullYear() - individualProfile.birthYear
          : undefined,
      avatar: individualProfile?.avatar || "initial",
    });
  }, [individualProfile]);

  function handleSaveIndividual(values: ProfileFormValues) {
    const trimmed = values.displayName.trim();
    if (!trimmed) {
      setProfileError("Merci d'indiquer un nom de profil.");
      return;
    }
    if (values.age !== undefined && values.age !== null) {
      if (values.age < 3 || values.age > 120) {
        setProfileError("Merci d'utiliser un âge entre 3 et 120 ans.");
        return;
      }
    }

    const updates: Partial<IndividualProfile> = {
      firstName: trimmed,
      avatar: values.avatar || "initial",
    };

    if (values.age !== undefined && values.age !== null) {
      const currentYear = new Date().getFullYear();
      (updates as any).birthYear = currentYear - values.age;
    }

    updateIndividualProfile(updates as any);
    setProfileMessage("Profil mis à jour (démonstration).");
    setProfileError(null);
    setEditOpen(false);
  }

  const profileName =
    individualProfile?.firstName || activeProfile?.firstName || "Profil MixMind";
  const accountLabel = account
    ? account.type === "individual"
      ? "Compte individuel"
      : account.type === "family"
      ? "Compte famille"
      : "Compte school"
    : "Visiteur";
  const themeLabel = darkMode
    ? "Mode sombre activé (par défaut)"
    : "Mode clair indisponible";

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-6 py-8 text-slate-900 dark:text-slate-50">
      <header className="space-y-1">
        <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Mon compte / Mon profil
        </p>
        <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-50">
          Espace compte
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Structure prête à accueillir l&apos;identité, les paramètres et les futures fonctionnalités.
        </p>
      </header>

      {/* Identité du profil */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">Identité du profil</h2>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-lg font-semibold">
            {profileName.charAt(0).toUpperCase()}
          </div>
          <div className="space-y-1 text-sm text-slate-700 dark:text-slate-300">
            <p className="font-semibold">{profileName}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{accountLabel}</p>
          </div>
        </div>
      </section>

      {/* Paramètres globaux */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">Paramètres globaux</h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
          Ici, vous pourrez ajuster vos préférences d&apos;affichage, la langue de l&apos;interface et d&apos;autres options générales
          associées à ce profil (fonctionnalités à venir).
        </p>
        <div className="grid gap-3 text-sm text-slate-700 dark:text-slate-300">
          <div className="flex items-center justify-between">
            <span>Mode d&apos;affichage</span>
            <button
              type="button"
              onClick={() => setDarkMode(false)}
              className={
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition " +
                (darkMode
                  ? "border-slate-600 bg-slate-800 text-slate-100"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
              }
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: darkMode ? "#0f172a" : "#e2e8f0" }} />
              <span>{themeLabel}</span>
            </button>
          </div>
          <div className="flex items-center justify-between">
            <span>Langue</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">FR (placeholder)</span>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Le mode clair est momentanément désactivé pour préserver la lisibilité en vitrine.
        </p>
      </section>

      {/* Mode d'affichage par âge */}
      {/* TODO MixMind: relier ce sélecteur à une prévisualisation filtrée des contenus par tranche d'âge. */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">
          Mode d&apos;affichage selon l&apos;âge (à venir)
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Vous pourrez bientôt choisir de visualiser MixMind comme un enfant, un ado ou un adulte, afin de prévisualiser les contenus
          adaptés à chaque tranche d&apos;âge.
        </p>
      </section>

      {/* Gestion du profil */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">Gestion du profil</h2>
        {isIndividual ? (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              Ajustez ici le profil principal : nom, avatar et âge estimé. Les comptes multi-profils conservent leur gestion dans l’espace famille / school.
            </p>
            <div className="flex items-center justify-between gap-3 text-sm text-slate-700 dark:text-slate-300">
              <div>
                <p className="font-semibold">{profileName}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Profil individuel</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditOpen(true);
                  setProfileError(null);
                  setProfileMessage(null);
                }}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-800 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
              >
                Modifier le profil
              </button>
            </div>
            {profileMessage && (
              <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">{profileMessage}</p>
            )}
            {editOpen && (
              <div className="mt-3">
                <ProfileForm
                  initialValues={profileInitial}
                  onSubmit={handleSaveIndividual}
                  onCancel={() => {
                    setEditOpen(false);
                    setProfileError(null);
                  }}
                  submitLabel="Enregistrer"
                  helperText="Met à jour l’identité utilisée sur MixMind."
                  errorText={profileError}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              Ajustez ici les paramètres du profil en cours (affichage, préférences, identité). Les comptes Famille ou School peuvent accéder à l&apos;espace dédié pour gérer plusieurs profils.
            </p>
            <Link
              to="/parents"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
            >
              Accéder à l&apos;espace Famille / School
            </Link>
          </>
        )}
      </section>

      {/* Activités */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">Mes activités (à venir)</h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Vous trouverez ici un résumé de vos thèmes, exercices et mini-jeux réalisés, ainsi que votre progression globale sur MixMind
          (fonctionnalité en préparation).
        </p>
      </section>

      {/* Paramètres de compte */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">
          Paramètres du compte (placeholder)
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Type de compte : Standard (placeholder). Plus tard, vous pourrez passer à un compte Premium, Famille ou School, et gérer vos
          options d&apos;abonnement depuis cette section.
        </p>
      </section>
    </div>
  );
}
